import React from 'react';
import html2canvas from 'html2canvas';
import {fetchImageAsBase64} from 'utils/imageUtils';

/**
 * 日付情報を含むフォーマット済み文字列を生成する
 * @returns {string} YYYYMMDD_HHmmss 形式の日付文字列
 */
const generateDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
};

/**
 * 画像をダウンロードする
 * @param {string} dataUrl 画像データURL
 * @param {string} fileName ファイル名（接頭辞）
 */
const downloadImage = (dataUrl: string, fileName: string): void => {
  const downloadLink = document.createElement('a');

  if (typeof downloadLink.download === 'string') {
    downloadLink.href = dataUrl;

    // 日付情報を含むファイル名を生成
    const dateStr = generateDateString();

    // ファイル名
    downloadLink.download = `LFC_${fileName}_${dateStr}.png`;

    // Firefox では body の中にダウンロードリンクがないといけないので一時的に追加
    document.body.appendChild(downloadLink);

    // ダウンロードリンクが設定された a タグをクリック
    downloadLink.click();

    // Firefox 対策で追加したリンクを削除しておく
    document.body.removeChild(downloadLink);
  } else {
    window.open(dataUrl);
  }
};

/**
 * html2canvasを使用してDOM要素をキャプチャし、画像として保存する
 * @param element キャプチャする要素
 * @param fileName ファイル名（接頭辞）
 * @param options html2canvasのオプション
 * @param mcElm mainContent要素（存在する場合）
 * @param originalOverflow mcElm存在時の元のoverflowスタイル
 */
const captureWithHtml2canvas = (
  element: HTMLElement,
  fileName: string,
  options: any,
  mcElm?: HTMLElement | null,
  originalOverflow?: string
): void => {
  html2canvas(element, options).then(canvas => {
    // mainContent要素が存在する場合、スクロールスタイルを元に戻す
    if (mcElm && originalOverflow !== undefined) {
      mcElm.style.overflow = originalOverflow;
    }

    // 複製したDOMを削除
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }

    // 画像のデータURLを取得
    const targetImgUri = canvas.toDataURL('image/png');

    // 共通化したダウンロード関数を使用
    downloadImage(targetImgUri, fileName);
  });
};

/**
 * キャプチャを出力する
 * @param targetId キャプチャするHTML要素のID
 * @param fileName ファイル名（接頭辞）
 */
export const saveAsCaptureImage = (targetId: string, fileName: string) => {
  // 画像に変換する component の id を指定
  const target = document.getElementById(targetId);

  // スクロールで隠れているところも取るように入れる
  let mcElm = document.getElementById('mainContent');
  if (mcElm) {
    mcElm.style.overflow = 'inherit';
  }

  html2canvas(target as HTMLElement, {scrollY: 0}).then(canvas => {
    // 戻す
    if (mcElm) {
      mcElm.style.overflow = 'auto';
    }

    const targetImgUri = canvas.toDataURL('image/png');

    // 共通化したダウンロード関数を使用
    downloadImage(targetImgUri, fileName);
  });
};

/**
 * 元の要素から計算済みスタイルを取得して適用する関数
 * @param sourceElement 元のHTML要素
 * @param targetElement コピー先のHTML要素
 */
const applyComputedStyles = (sourceElement: HTMLElement, targetElement: HTMLElement) => {
  // 再帰的に全ての子要素にスタイルを適用
  const applyStylesToElement = (source: Element, target: Element) => {
    if (source instanceof HTMLElement && target instanceof HTMLElement) {
      const computedStyle = window.getComputedStyle(source);

      // 背景色を適用
      if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
        target.style.backgroundColor = computedStyle.backgroundColor;
      }

      // 文字色を適用
      if (computedStyle.color) {
        target.style.color = computedStyle.color;
      }

      // ボーダーを適用
      if (computedStyle.borderWidth && computedStyle.borderStyle && computedStyle.borderColor) {
        target.style.borderWidth = computedStyle.borderWidth;
        target.style.borderStyle = computedStyle.borderStyle;
        target.style.borderColor = computedStyle.borderColor;
      }

      // 子要素に対しても同様に処理
      for (let i = 0; i < Math.min(source.children.length, target.children.length); i++) {
        applyStylesToElement(source.children[i], target.children[i]);
      }
    }
  };

  applyStylesToElement(sourceElement, targetElement);
};

/**
 * Material-UI特有の要素にスタイルを適用する関数
 * 元の要素の計算済みスタイルを優先的に使用
 * セレクタリストと再利用可能なヘルパー関数を使用してコードの重複を削減
 * @param element HTML要素
 * @param originalElement 元のHTML要素（計算済みスタイルを取得するため）
 */
const applyMaterialUIStyles = (element: HTMLElement, originalElement: HTMLElement) => {
  // 単一要素に背景色を適用するヘルパー関数
  const applyBackgroundColor = (selector: string) => {
    const targetElement = element.querySelector(selector);
    const originalTargetElement = originalElement.querySelector(selector);

    if (targetElement instanceof HTMLElement && originalTargetElement instanceof HTMLElement) {
      const computedStyle = window.getComputedStyle(originalTargetElement);
      if (
        computedStyle.backgroundColor &&
        computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
        computedStyle.backgroundColor !== 'transparent'
      ) {
        targetElement.style.backgroundColor = computedStyle.backgroundColor;
      }
    }
  };

  // 複数要素に背景色を適用するヘルパー関数
  const applyBackgroundColorToMultiple = (selector: string) => {
    const targetElements = element.querySelectorAll(selector);
    const originalTargetElements = originalElement.querySelectorAll(selector);

    for (let i = 0; i < Math.min(targetElements.length, originalTargetElements.length); i++) {
      const targetElement = targetElements[i];
      const originalTargetElement = originalTargetElements[i];

      if (targetElement instanceof HTMLElement && originalTargetElement instanceof HTMLElement) {
        const computedStyle = window.getComputedStyle(originalTargetElement);
        if (
          computedStyle.backgroundColor &&
          computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
          computedStyle.backgroundColor !== 'transparent'
        ) {
          targetElement.style.backgroundColor = computedStyle.backgroundColor;
        }
      }
    }
  };

  // 単一要素のセレクタリスト
  const singleElementSelectors = [
    '.MuiPaper-root', // ダイアログのPaper要素
    '.MuiDialogContent-root', // ダイアログのコンテンツ要素
    '.MuiDialogTitle-root' // ダイアログのタイトル要素
  ];

  // 複数要素のセレクタリスト
  const multipleElementSelectors = [
    '.MuiDataGrid-root' // データグリッド要素
  ];

  // 単一要素に背景色を適用
  singleElementSelectors.forEach(selector => {
    applyBackgroundColor(selector);
  });

  // 複数要素に背景色を適用
  multipleElementSelectors.forEach(selector => {
    applyBackgroundColorToMultiple(selector);
  });
};

/**
 * クロスオリジン画像を含むコンテンツをキャプチャして保存する
 * DOM複製アプローチを使用して画面のちらつきを防止
 * Material-UIのテーマシステムから直接背景色を取得
 * @param targetId キャプチャするHTML要素のID
 * @param fileName ファイル名（接頭辞）
 * @param backgroundColor 背景色（Material-UIのテーマから取得）
 */
export const saveAsCrossOriginCaptureImage = async (
  targetId: string,
  fileName: string,
  backgroundColor?: string
) => {
  // 画像に変換する component の id を指定
  const target = document.getElementById(targetId);
  if (!target) return;

  try {
    // ターゲット要素を複製
    const clonedTarget = target.cloneNode(true) as HTMLElement;

    // 元の要素から計算済みスタイルを取得して適用
    applyComputedStyles(target, clonedTarget);

    // Material-UI特有の要素にスタイルを適用（元の要素の計算済みスタイルを使用）
    applyMaterialUIStyles(clonedTarget, target);

    // 複製したDOMを画面外に配置
    clonedTarget.style.position = 'absolute';
    clonedTarget.style.left = '-9999px';
    clonedTarget.style.top = '-9999px';
    document.body.appendChild(clonedTarget);

    // 複製したDOM内の不要な要素を削除
    const captureButtons = clonedTarget.querySelectorAll('[aria-label="capture"]');
    const closeButtons = clonedTarget.querySelectorAll('[aria-label="close"]');
    const toolbarContainer = clonedTarget.querySelectorAll(`.MuiDataGrid-toolbarContainer`);

    // キャプチャボタンを削除
    captureButtons.forEach(button => {
      if (button.parentNode) {
        button.parentNode.removeChild(button);
      }
    });

    // クローズボタンを削除
    closeButtons.forEach(button => {
      if (button.parentNode) {
        button.parentNode.removeChild(button);
      }
    });

    // ツールバー（CSV/Excel出力ボタンを含む）を削除
    toolbarContainer.forEach(container => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    });

    // 複製したDOM内の全ての画像要素を取得
    const images = clonedTarget.querySelectorAll('img');

    // 画像をBase64に変換する処理
    const conversionPromises = Array.from(images).map(async (img: HTMLImageElement) => {
      if (img.src && !img.src.startsWith('data:')) {
        try {
          // 画像をBase64に変換
          const base64Image = await fetchImageAsBase64(img.src);

          // ソースを置き換え
          return new Promise<void>(resolve => {
            const newImg = new Image();

            newImg.onload = () => {
              img.src = base64Image;
              resolve();
            };

            newImg.onerror = () => {
              console.error('Error loading converted image');
              resolve(); // エラー時も続行
            };

            newImg.src = base64Image;
          });
        } catch (error) {
          console.error('Error converting image:', error);
          // エラーでも続行
        }
      }
    });

    // 全ての画像変換が完了するのを待つ
    await Promise.all(conversionPromises.filter(Boolean));

    // html2canvas のオプションにテーマから取得した背景色を設定
    const html2canvasOptions = {
      scrollY: 0,
      useCORS: true, // クロスオリジン画像を許可
      allowTaint: true, // 外部ドメインのコンテンツによるキャンバスの汚染を許可
      logging: false, // デバッグログを無効化
      imageTimeout: 0, // 画像読み込みのタイムアウトを無効化
      scale: window.devicePixelRatio, // より鮮明な画像用に画面のピクセル比を使用
      backgroundColor: backgroundColor || undefined // テーマから取得した背景色を設定（未指定の場合はundefined）
    };

    // mainContent要素の処理
    let mcElm = document.getElementById('mainContent');
    let originalOverflow;

    if (mcElm) {
      // 元のスタイルを保存
      originalOverflow = mcElm.style.overflow;
      mcElm.style.overflow = 'inherit';
    }

    // 共通関数を使用してキャプチャを実行
    captureWithHtml2canvas(clonedTarget, fileName, html2canvasOptions, mcElm, originalOverflow);
  } catch (error) {
    console.error('Error during capture:', error);
  }
};

/**
 * 引数
 */
interface Props {
  id: string;
  children: React.ReactNode;
}

/**
 * ScreenCapture
 * @returns
 */
const ScreenCapture = (props: Props) => {
  // 出力予定のコンポーネントのルート階層に id を付けておく
  return <div id={props.id}>{props.children}</div>;
};

export default ScreenCapture;
